import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { useDateFormatter } from '../../generic/hooks/useDateFormatter'
import { localeCodeOf } from '../../generic/utilities/customLanguage'
import { formatDuration } from '../model/durationFormat'
import {
  DAY_LABEL_OPTIONS,
  DEFAULT_VIEWPORT_WIDTH,
  LABEL_COLUMN_WIDTH,
  centerInstantOf,
  intlLocaleOf,
  localDayStarts,
  pxPerMsFor,
  scrollLeftFor,
  sixHourMarks
} from '../model/timeGrid'
import TimelineBlock from './TimelineBlock'
import './timeline.css'

const pad = (value) => String(value).padStart(2, '0')
// «View on the timeline» (RN16): how much of the track stays visible before the start of the stretch.
const FOCUS_LEAD_PX = 120

const clockOf = (ms) => `${pad(new Date(ms).getHours())}:${pad(new Date(ms).getMinutes())}`

// The timeline itself (RN22-RN36): axis with the day labels and the 6 h ticks, the «now» line of the response, the blocks,
// and ONE tooltip shared by every segment (T-3), found by delegation so that hovering or focusing a segment costs nothing
// per segment. The container scrolls both ways; the label column stays put (RN34).
const TimelineGrid = ({ view, range, zoomDays, dimmed, selection, overlay, focus, onSelect, onDrag, onNudge }) => {
  const { t_, i18n } = useSmartTranslation()
  const { formatDateTime, formatTime } = useDateFormatter()
  const scrollRef = useRef(null)
  const [viewportWidth, setViewportWidth] = useState(DEFAULT_VIEWPORT_WIDTH)
  const [hover, setHover] = useState({ ref: '', x: 0, y: 0 })

  const trackViewportWidth = viewportWidth - LABEL_COLUMN_WIDTH
  const rangeMs = range.toMs - range.fromMs
  const pxPerMs = pxPerMsFor({ zoomDays, trackViewportWidth, rangeMs })
  const trackWidth = Math.round(rangeMs * pxPerMs)

  // The viewport width: measured when the browser can say, and kept up to date when it changes. Never wider than the
  // window: if the page ever lets the scroller grow with its own track, each measure would widen the track again.
  useLayoutEffect(() => {
    const element = scrollRef.current
    if (!element) return undefined
    const measure = () => {
      const windowWidth = document.documentElement.clientWidth
      const ownWidth = element.clientWidth || DEFAULT_VIEWPORT_WIDTH
      setViewportWidth(windowWidth > 0 ? Math.min(ownWidth, windowWidth) : ownWidth)
    }
    measure()
    if (typeof ResizeObserver !== 'function') return undefined
    const observer = new ResizeObserver(measure)
    observer.observe(element)
    return () => observer.disconnect()
  }, [])

  // Changing the zoom keeps the instant at the centre of the view (RN12).
  const lastScale = useRef({ pxPerMs, fromMs: range.fromMs })
  useLayoutEffect(() => {
    const element = scrollRef.current
    const previous = lastScale.current
    lastScale.current = { pxPerMs, fromMs: range.fromMs }
    if (!element || previous.pxPerMs === pxPerMs || previous.fromMs !== range.fromMs) return
    const centerMs = centerInstantOf({ scrollLeft: element.scrollLeft, trackViewportWidth, pxPerMs: previous.pxPerMs, fromMs: range.fromMs })
    element.scrollLeft = scrollLeftFor({ centerMs, trackViewportWidth, pxPerMs, fromMs: range.fromMs })
  }, [pxPerMs, range.fromMs, trackViewportWidth])

  // «View on the timeline»: once the reading of the asked period is painted, the start of the stretch is made visible (once per
  // request). It is published (`data-focus-ms`, `data-focus-left`) so that a test can read where the grid was taken.
  // The grid can be painted before its viewport is measured (the default width): when the measured width changes the scale, the
  // same request is applied again with the real one, so the stretch stays in view (seen in the S62 screen test).
  const [focused, setFocused] = useState({ token: 0, ms: 0, left: 0 })
  const focusedRequest = useRef({ token: 0, trackViewportWidth: 0 })
  useLayoutEffect(() => {
    const element = scrollRef.current
    if (!element || !focus || focus.token === 0 || focus.fromMs !== range.fromMs) return
    const done = focusedRequest.current
    if (done.token === focus.token && done.trackViewportWidth === trackViewportWidth) return
    focusedRequest.current = { token: focus.token, trackViewportWidth }
    const left = Math.max(0, (focus.ms - range.fromMs) * pxPerMs - FOCUS_LEAD_PX)
    element.scrollLeft = left
    setFocused({ token: focus.token, ms: focus.ms, left })
  }, [focus, range.fromMs, pxPerMs, trackViewportWidth])

  const locale = intlLocaleOf(localeCodeOf(i18n.language))
  const dayFormat = useMemo(() => new Intl.DateTimeFormat(locale, DAY_LABEL_OPTIONS), [locale])
  const days = useMemo(() => localDayStarts(range.fromMs, range.toMs), [range.fromMs, range.toMs])
  const marks = useMemo(() => sixHourMarks(range.fromMs, range.toMs), [range.fromMs, range.toMs])
  const dayStarts = useMemo(() => new Set(days), [days])

  const segmentIndex = useMemo(() => {
    const index = new Map()
    view.blocks.forEach((block) => block.lanes.forEach((lane) => lane.segments.forEach((segment) => {
      index.set(`${block.key}|${lane.key}|${segment.key}`, segment)
    })))
    return index
  }, [view])

  const showTooltip = useCallback((event) => {
    const target = event.target.closest ? event.target.closest('[data-seg]') : null
    if (!target) return
    const rect = event.type === 'focus' || event.type === 'focusin' ? target.getBoundingClientRect() : { left: event.clientX, bottom: event.clientY }
    setHover({ ref: target.getAttribute('data-seg'), x: rect.left, y: rect.bottom })
  }, [])
  const hideTooltip = useCallback(() => setHover((current) => (current.ref ? { ref: '', x: 0, y: 0 } : current)), [])

  // Pressing, or Enter / Space on, a segment that opens an intervention selects it (RN37): found by delegation too.
  const selectFrom = useCallback((event) => {
    const target = event.target.closest ? event.target.closest('[data-seg]') : null
    if (!target || target.getAttribute('role') !== 'button') return false
    const ref = target.getAttribute('data-seg')
    const segment = segmentIndex.get(ref)
    if (!segment || segment.interventionIndex < 0) return false
    onSelect(ref.split('|')[0], segment.interventionIndex)
    return true
  }, [segmentIndex, onSelect])
  const handleClick = useCallback((event) => { selectFrom(event) }, [selectFrom])
  const handleKeyDown = useCallback((event) => {
    if ((event.key === 'Enter' || event.key === ' ') && selectFrom(event)) event.preventDefault()
  }, [selectFrom])

  const hovered = segmentIndex.get(hover.ref)
  const nowInRange = view.nowMs >= range.fromMs && view.nowMs <= range.toMs

  return (
    <div
      ref={scrollRef}
      className={`dc-tl${dimmed ? ' dc-tl--dim' : ''}`}
      data-focus-ms={focused.token ? focused.ms : undefined}
      data-focus-left={focused.token ? Math.round(focused.left) : undefined}
      onMouseOver={showTooltip}
      onMouseOut={hideTooltip}
      onFocus={showTooltip}
      onBlur={hideTooltip}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    >
      <div className='dc-tl-inner'>
        <div style={{ position: 'absolute', top: 0, bottom: 0, left: LABEL_COLUMN_WIDTH, width: trackWidth, pointerEvents: 'none' }}>
          {marks.map((mark) => (
            <span
              key={mark}
              className={`dc-gridline${dayStarts.has(mark) ? ' dc-gridline--day' : ''}`}
              data-testid='grid-line'
              data-kind={dayStarts.has(mark) ? 'day' : 'six-hours'}
              data-instant={mark}
              style={{ left: (mark - range.fromMs) * pxPerMs }}
            />
          ))}
          {nowInRange && <span className='dc-now' data-testid='now-line' data-instant={view.nowMs} style={{ left: (view.nowMs - range.fromMs) * pxPerMs }} />}
        </div>
        <div className='dc-row dc-axis'>
          <div className='dc-cell-l'>{view.labelHeader}</div>
          <div className='dc-track' style={{ width: trackWidth }}>
            {days.map((day) => (
              <span key={day} className='dc-axis-day' data-testid='axis-day' data-instant={day} style={{ left: (day - range.fromMs) * pxPerMs }}>{dayFormat.format(new Date(day))}</span>
            ))}
            {marks.filter((mark) => !dayStarts.has(mark)).map((mark) => (
              <span key={mark} className='dc-axis-hour' style={{ left: (mark - range.fromMs) * pxPerMs }}>{clockOf(mark)}</span>
            ))}
            {nowInRange && (
              <span className='dc-now-label' data-testid='now-label' style={{ left: (view.nowMs - range.fromMs) * pxPerMs }}>
                {t_('now · {{time}}', { time: formatTime(view.nowMs) })}
              </span>
            )}
          </div>
        </div>
        {view.blocks.map((block) => (
          <TimelineBlock key={block.key} block={block} fromMs={range.fromMs} pxPerMs={pxPerMs} trackWidth={trackWidth} selection={selection && selection.blockKey === block.key ? selection : undefined} overlay={selection && selection.blockKey === block.key ? overlay : undefined} onDrag={onDrag} onNudge={onNudge} />
        ))}
      </div>
      {hovered && (
        <div role='tooltip' className='dc-tooltip' style={{ left: hover.x + 12, top: hover.y + 12 }}>
          <div><b>{hovered.tooltip.lane}</b></div>
          <div>{[hovered.tooltip.what, hovered.tooltip.caseText].filter(Boolean).join(' · ')}</div>
          <div>{`${formatDateTime(hovered.realStartMs)} – ${hovered.open ? t_('in progress') : formatDateTime(hovered.realEndMs)}`}</div>
          <div>{formatDuration(hovered.durationMs, t_)}</div>
          {hovered.tooltip.markText && <div>{hovered.tooltip.markText}</div>}
        </div>
      )}
    </div>
  )
}

export default TimelineGrid
